import React from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/lib/i18n";
import {
  LayoutDashboard, Gamepad2, Users, Award, MessageSquare, Grid3x3, FlaskConical, LogOut,
  Sparkles, FileQuestion, PanelBottom, Zap, Trophy, UserCheck, Settings, Crown, Bot, Dice5,
  UsersRound, Languages, Database, CloudOff, School, Search, Megaphone, Activity, CreditCard,
  FileText, GraduationCap,
} from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const sections = [
  {
    key: "section_general",
    items: [
      { to: "/admin", icon: LayoutDashboard, key: "dashboard", end: true },
      { to: "/admin/metricas", icon: Activity, key: "metrics" },
      { to: "/admin/definicoes-principal", icon: Settings, key: "main_settings" },
      { to: "/admin/publicidade", icon: Megaphone, key: "ads" },
      { to: "/admin/intro", icon: Zap, key: "intro" },
    ],
  },
  {
    key: "section_games_content",
    items: [
      { to: "/admin/jogos", icon: Gamepad2, key: "games" },
      { to: "/admin/grande-final", icon: Trophy, key: "grande_final" },
      { to: "/admin/perguntas", icon: FileQuestion, key: "questions" },
      { to: "/admin/eliminatorias", icon: Crown, key: "elim_challenges" },
      { to: "/admin/robots", icon: Bot, key: "robots" },
      { to: "/admin/roleta", icon: Dice5, key: "roulette" },
      { to: "/admin/diplomas", icon: Award, key: "diplomas" },
      { to: "/admin/resultados", icon: Trophy, key: "results" },
    ],
  },
  {
    key: "section_users",
    items: [
      { to: "/admin/credenciais", icon: Users, key: "credentials" },
      { to: "/admin/inscricoes", icon: School, key: "inscriptions" },
      { to: "/admin/pagamentos", icon: CreditCard, key: "payments" },
      { to: "/admin/utilizadores-vs-robot", icon: UsersRound, key: "vsrobot_users" },
      { to: "/admin/utilizadores-vs-humano", icon: UserCheck, key: "vshuman_users" },
      { to: "/admin/utilizadores-treino", icon: UserCheck, key: "training_users" },
      { to: "/admin/utilizadores-competicao", icon: Trophy, key: "competition_users" },
      { to: "/admin/eliminatorias-escolares", icon: Crown, key: "elim_users" },
      { to: "/admin/testes", icon: FlaskConical, key: "test_zone" },
    ],
  },
  {
    key: "section_texts",
    items: [
      { to: "/admin/mensagens", icon: MessageSquare, key: "messages" },
      { to: "/admin/regulamento", icon: FileText, key: "regulamento" },
      { to: "/admin/demo-frases", icon: GraduationCap, key: "demo_phrases" },
      { to: "/admin/pesquisar-editar", icon: Search, key: "search_edit_texts" },
      { to: "/admin/i18n", icon: Languages, key: "i18n_coverage" },
      { to: "/admin/migracao", icon: Database, key: "db_migration" },
      { to: "/admin/resultados-pendentes", icon: CloudOff, key: "pending_results" },
    ],
  },
];

export default function AdminLayout() {
  const { adminAuth, logoutAdmin } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();

  if (!adminAuth || adminAuth.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex bg-slate-50 font-[Nunito]">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-pink-500 grid place-items-center text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="font-[Outfit] font-bold text-slate-900">SUPERTMATIK</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-2">Backoffice</div>
        </div>
        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {sections.map((sec) => (
            <div key={sec.key} data-testid={`nav-section-${sec.key}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 px-3 mb-1">
                {t(sec.key)}
              </div>
              <div className="space-y-1">
                {sec.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`nav-${it.key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-100"
                      }`
                    }
                  >
                    <it.icon className="w-4 h-4" />
                    <span>{t(it.key)}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200 space-y-2">
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { logoutAdmin(); nav("/"); }}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-600 w-full"
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
